import React from 'react';
import clsx from 'clsx';
import { makeStyles } from '@lifeomic/chroma-react/styles';

/**
 * The EKG / heartbeat waveform traced by the loader. Authored against a
 * `0 0 100 28` viewBox and normalized via `pathLength={100}` so the animated
 * "sweep" math is independent of the real geometry.
 */
const EKG_PATH = 'M0 14 H34 l3 -1 l2 3 l3 -13 l3 24 l3 -13 l3 0 H100';

const useStyles = makeStyles(
  theme => ({
    root: {
      display: 'inline-block',
      verticalAlign: 'middle',
      // Default the waveform to the theme primary color. Consumers can override
      // by passing `color`, which sets `currentColor` on the svg.
      color: theme.palette.primary.main,
    },
    track: {
      fill: 'none',
      stroke: 'currentColor',
      opacity: 0.2,
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    pulse: {
      fill: 'none',
      stroke: 'currentColor',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      // A short lit segment (25 of the normalized 100) sweeps across the
      // otherwise-dim track, leaving a large gap so only one beat shows.
      strokeDasharray: '25 100',
      animation: '$pulseSweep var(--pulse-loader-speed, 1.6s) linear infinite',
    },
    '@keyframes pulseSweep': {
      '0%': {
        strokeDashoffset: 25,
      },
      '100%': {
        strokeDashoffset: -100,
      },
    },
    '@media (prefers-reduced-motion: reduce)': {
      pulse: {
        animation: 'none',
        strokeDasharray: 'none',
        opacity: 1,
      },
      track: {
        opacity: 0,
      },
    },
  }),
  { name: 'PulseLoader' },
);

export interface PulseLoaderProps
  extends Omit<React.ComponentPropsWithoutRef<'svg'>, 'width' | 'height'> {
  /** Rendered width in pixels. Height scales to keep the waveform aspect ratio. */
  width?: number;
  /** Stroke thickness of the waveform. */
  strokeWidth?: number;
  /** Duration of a single heartbeat sweep, in seconds. */
  speed?: number;
  /** Accessible label announced to assistive tech. */
  label?: string;
  /**
   * Waveform color. Defaults to the theme primary color. Accepts any CSS color
   * value (including `'currentColor'` to inherit the surrounding text color).
   */
  color?: string;
  /** Class applied to the animated pulse path (e.g. to override the color). */
  pulseClassName?: string;
}

/**
 * A Pulse Loader provides a visual cue that an action is processing, drawn as an
 * animated EKG-style heartbeat. The waveform inherits `currentColor`, so it
 * adopts the surrounding text color when placed inside a button or label.
 *
 * It mirrors the role of chroma-react's `DotLoader` but is purpose-built for
 * Zori Pulse surfaces.
 */
export const PulseLoader: React.FC<PulseLoaderProps> = ({
  width = 48,
  strokeWidth = 2,
  speed = 1.6,
  label = 'Loading',
  color,
  className,
  pulseClassName,
  style,
  ...rest
}) => {
  const classes = useStyles();
  const height = Math.round((width * 28) / 100);

  return (
    <svg
      role="img"
      aria-label={label}
      viewBox="0 0 100 28"
      width={width}
      height={height}
      preserveAspectRatio="xMidYMid meet"
      className={clsx(classes.root, className)}
      style={
        {
          '--pulse-loader-speed': `${speed}s`,
          ...(color ? { color } : {}),
          ...style,
        } as React.CSSProperties
      }
      {...rest}
    >
      <path
        d={EKG_PATH}
        pathLength={100}
        strokeWidth={strokeWidth}
        className={classes.track}
      />
      <path
        d={EKG_PATH}
        pathLength={100}
        strokeWidth={strokeWidth}
        className={clsx(classes.pulse, pulseClassName)}
      />
    </svg>
  );
};

export default PulseLoader;
